import type { ReactNode } from 'react'

import { cn } from '@maxus/website/utils'

import { Badge } from '../base'

type MaxusChipVariant =
  | 'default'
  | 'latest'
  | 'stage-demo'
  | 'stage-a-copy'
  | 'stage-b-copy'
  | 'stage-distribution'
  | 'status-uploading'
  | 'status-rendering'
  | 'status-success'
  | 'status-failed'
  | 'health-normal'
  | 'health-abnormal'
  | 'final'
  | 'pending'
  | 'count'
  | 'platform'
  | 'version'

const chipStyles: Record<MaxusChipVariant, string> = {
  default: 'border-border-strong bg-surface-elevated text-text-secondary',
  latest: 'border-blue-500/70 bg-blue-500/10 text-blue-300',
  'stage-demo': 'border-transparent bg-surface-active text-text-secondary',
  'stage-a-copy': 'border-transparent bg-[#243659] text-[#8bb7ff]',
  'stage-b-copy': 'border-transparent bg-[#2d2368] text-[#a59bff]',
  'stage-distribution': 'border-transparent bg-[#123524] text-[#5fe08a]',
  'status-uploading': 'border-blue-500/35 bg-blue-500/10 text-blue-300',
  'status-rendering': 'border-violet-500/35 bg-violet-500/15 text-violet-200',
  'status-success': 'border-status-resolved/35 bg-status-resolved/15 text-status-resolved',
  'status-failed': 'border-status-danger/35 bg-status-danger/15 text-status-danger',
  'health-normal': 'border-status-resolved/35 bg-status-resolved/15 text-status-resolved',
  'health-abnormal': 'border-status-danger/35 bg-status-danger/15 text-status-danger',
  final: 'border-transparent bg-transparent text-status-resolved',
  pending: 'border-transparent bg-transparent text-status-pending',
  count: 'border-border-strong bg-surface-elevated text-text-tertiary',
  platform: 'border-border-strong bg-surface-elevated text-text-secondary',
  version: 'border-border-strong bg-surface-elevated text-text-secondary',
}

type MaxusChipProps = {
  children: ReactNode
  className?: string
  icon?: ReactNode
  size?: 'sm' | 'md'
  variant?: MaxusChipVariant
}

const MaxusChip = ({
  children,
  className,
  icon,
  size = 'md',
  variant = 'default',
}: MaxusChipProps) => {
  return (
    <Badge
      variant="outline"
      className={cn(
        'inline-flex shrink-0 items-center gap-1 !rounded-[8px] border font-normal',
        size === 'md' ? 'h-7 px-2.5 text-xs' : 'h-[22px] px-2 text-xs',
        size === 'sm' && variant === 'count' && 'gap-0.5',
        chipStyles[variant],
        className,
      )}
    >
      {icon}
      {children}
    </Badge>
  )
}

export { MaxusChip, type MaxusChipVariant }
